/**
 * Coordinates one Auto Assign review session and exposes its view state and user actions.
 *
 * @remarks
 * `review-state.ts` owns synchronous transitions and undo history. This hook derives
 * readiness, coordinates itinerary operations and assignment/journey choices, and invokes the
 * caller's save callback. JSX and label formatting belong in `../review-dialog.tsx`.
 *
 * @packageDocumentation
 */
import {useReducer, useRef} from 'react';
import {
    AssignmentPlan,
    AssignmentRow,
    assignmentKey,
} from '../../../../../common/flight-leg/assignment-plan';
import {reconcileVisits} from '../../../../../common/flight-leg/route-visits';
import type {PartialFlight} from '../../../../flights/scheduling/scheduled-paxcgo-adder/hook';
import {addJourneyVisits, assignmentPayload, journeyGroups, repeatedJourneyLocations, suggestedLegs} from './journeys';
import {changeTripType, moveRouteLeg, replanVisits} from './itinerary';
import {AutoAssignDraft, autoAssignReducer, createAutoAssignState} from './review-state';

/** Initial route data and persistence callbacks for one Auto Assign review session. */
export interface AutoAssignReviewOptions {
    initialPlan: AssignmentPlan;
    currentRoutePlan?: AssignmentPlan;
    returnToOrigin: boolean;
    /** External mode keeps authored stops in the route drawer and only reviews assignments here. */
    routeEditing?: 'inline' | 'external';
    names: Map<string, string>;
    isCurrent: () => boolean;
    save: (flight: PartialFlight) => Promise<void>;
    onClose: (saved: boolean) => void;
}

/**
 * Maps a resolved assignment to a radio value without changing its memberships.
 *
 * @remarks
 * Unresolved rows and assignments spanning separate ranges have no single radio
 * value. An undefined value does not itself invalidate preserved manual assignments.
 */
const currentChoice = (row: AssignmentRow): number | 'unassign' | undefined => {
    if (row.status === 'review') return undefined;
    if (row.status === 'unassigned') return 'unassign';
    if (row.ranges.length !== 1) return undefined;

    const index = row.candidates.findIndex(
        (range) =>
            range.from.key === row.ranges[0].from.key && range.to.key === row.ranges[0].to.key
    );

    return index < 0 ? undefined : index;
};

/**
 * Coordinates route drafts, assignment choices, history, navigation, and saving.
 *
 * @remarks
 * Direct journeys remain pending until final review. Save failures retain the draft,
 * and a synchronous ref prevents duplicate saves before React renders the next state.
 */
export function useAutoAssignReview(props: AutoAssignReviewOptions) {
    const [state, dispatch] = useReducer(
        autoAssignReducer,
        {
            plan: props.initialPlan,
            roundTrip: props.returnToOrigin,
            extend: props.routeEditing !== 'external',
        },
        createAutoAssignState
    );
    const {
        draft,
        past,
        future,
        step,
        finalPlan,
        showSteps,
        reviewKeys,
        selectionView,
        saving,
        error,
        saveFailed,
    } = state;

    // A ref closes the synchronous gap before React renders the saving state.
    const savingRef = useRef(false);

    const {roundTrip, extend} = draft;
    const plan = step === 2 && finalPlan ? finalPlan : draft.plan;
    const directJourneys = draft.directJourneys || [];
    const choiceFor = (row: AssignmentRow) =>
        directJourneys.includes(assignmentKey(row)) ? 'extend' : currentChoice(row);
    const unresolved = (row: AssignmentRow) =>
        row.status === 'review' && !directJourneys.includes(assignmentKey(row));

    const groups = journeyGroups(plan, reviewKeys);
    const pending = plan.rows.filter((row) => row.status === 'review');
    const unassigned = plan.rows.filter((row) => row.status === 'unassigned');
    const targets = plan.rows.filter((row) => reviewKeys.includes(assignmentKey(row)));
    const count = (rows: AssignmentRow[], kind: 'pax' | 'cgo') =>
        rows.filter((row) => row.kind === kind).length;

    const final = step === 2;
    const canSave = !pending.length && (final || (!showSteps && !directJourneys.length));
    const editable = !saving && step === 0 && props.routeEditing !== 'external';
    const legs = suggestedLegs(plan, props.names);

    function editRoute(operation: () => AutoAssignDraft) {
        if (savingRef.current) return;

        try {
            dispatch({type: 'edit-route', draft: operation()});
        } catch (error) {
            dispatch({type: 'error', message: error.message});
        }
    }

    /** Records a visit choice; a direct-journey choice leaves the itinerary unchanged. */
    function choose(rows: AssignmentRow[], choice: number | 'unassign' | 'extend') {
        if (savingRef.current || (choice === 'extend' && props.routeEditing === 'external')) return;

        dispatch({type: 'choose', keys: rows.map(assignmentKey), choice});
    }

    /**
     * Opens visit choices or prepares the final route from the current draft and pending choices.
     *
     * @remarks
     * Direct journeys are materialized only in the final projection. Returning to
     * choices keeps the original draft, so changing a choice leaves no added visits.
     * This transition never saves the flight.
     */
    function continueReview() {
        if (savingRef.current || (step === 1 && pending.some(unresolved))) return;

        if (step === 0 && (pending.length || directJourneys.length)) {
            dispatch({type: 'review-choices'});
            return;
        }

        try {
            let next = draft.plan;
            journeyGroups(draft.plan, directJourneys).forEach((rows) => {
                next = addJourneyVisits(next, rows, roundTrip, extend);
            });

            dispatch({type: 'review-final', plan: next});
        } catch (error) {
            dispatch({type: 'error', message: error.message});
        }
    }

    async function save() {
        if (savingRef.current || !canSave) return;
        if (!props.isCurrent()) {
            dispatch({
                type: 'error',
                message:
                    'The flight changed while you were reviewing. Cancel and reopen the review to load the current flight.',
            });
            return;
        }

        savingRef.current = true;
        dispatch({type: 'save-started'});

        try {
            await props.save(assignmentPayload(plan, props.names));
            props.onClose(true);
        } catch (error) {
            dispatch({
                type: 'save-failed',
                message:
                    'Unable to save the route and assignments. Your draft is kept. Retry saving or edit the draft.',
            });
        } finally {
            savingRef.current = false;
            dispatch({type: 'save-finished'});
        }
    }

    function setRoundTrip(value: boolean) {
        editRoute(() => ({...draft, roundTrip: value, plan: changeTripType(plan, value)}));
    }

    /**
     * Switches between extending the generated route and preserving the saved route.
     *
     * @remarks
     * Reconciles visit identities before replanning so valid assignment choices
     * survive the policy change; invalidated choices remain subject to review.
     */
    function setExtend(allow: boolean) {
        const target = allow ? props.initialPlan : props.currentRoutePlan;
        if (!target) return;

        editRoute(() => {
            const visits = reconcileVisits(
                plan.visits,
                target.visits.map((visit) => visit.locationID)
            );

            return {
                extend: allow,
                roundTrip,
                plan: changeTripType(replanVisits(plan, visits), roundTrip),
            };
        });
    }

    function reorder(from: number, to: number): string | undefined {
        if (savingRef.current) return;

        try {
            dispatch({
                type: 'edit-route',
                draft: {...draft, plan: moveRouteLeg(plan, from, to, roundTrip)},
            });
        } catch (error) {
            return error.message;
        }
    }

    function cancel() {
        if (!savingRef.current) props.onClose(false);
    }

    function back() {
        if (savingRef.current) return;

        if (step > 0) dispatch({type: 'back'});
        else cancel();
    }

    return {
        plan,
        groups,
        pending,
        step,
        showSteps,
        selectionView,
        saving,
        error,
        saveFailed,
        extend,
        final,
        editable,
        canSave,
        needsVisitChoices: step === 0 && (pending.length > 0 || directJourneys.length > 0),
        canContinue: !saving && !(step === 1 && pending.some(unresolved)),
        completedChoices: targets.filter((row) => !unresolved(row)).length,
        totalChoices: targets.length,
        pendingPassengerCount: count(pending, 'pax'),
        pendingCargoCount: count(pending, 'cgo'),
        repeatedLocations: repeatedJourneyLocations(targets),

        choiceFor,
        isMixed: (rows: AssignmentRow[]) =>
            rows.some((item) => !unresolved(item)) &&
            rows.some((item) => choiceFor(item) !== choiceFor(rows[0])),

        choose,
        continueReview,
        save,
        cancel,
        back,
        setExtend,
        edit: () => {
            if (savingRef.current) return;

            if (props.routeEditing === 'external') props.onClose(false);
            else dispatch({type: 'edit'});
        },
        setSelectionView: (view: string) => dispatch({type: 'view', view}),

        previewProps: {
            legs,
            passengerCount: new Set(legs.flatMap((leg) => leg.paxIDs)).size,
            cargoCount: new Set(legs.flatMap((leg) => leg.cgoIDs)).size,
            omittedPassengerCount: count(unassigned, 'pax'),
            omittedCargoCount: count(unassigned, 'cgo'),
            returnToOrigin: roundTrip,
            returnLocationName: props.names.get(plan.visits[0]?.locationID),
            readOnly: !editable,
            onReturnToOriginChange: editable ? setRoundTrip : undefined,
            onReorder: editable ? reorder : undefined,
            canUndo: past.length > 0,
            canRedo: future.length > 0,
            onUndo: editable ? () => dispatch({type: 'undo'}) : undefined,
            onRedo: editable ? () => dispatch({type: 'redo'}) : undefined,
        },
    };
}

/** View state and semantic actions consumed by the dialog shell and its steps. */
export type AutoAssignReview = ReturnType<typeof useAutoAssignReview>;
